import { apiClient } from '../../lib/apiClient';
import { createCollectionApi } from '../knowledge-base/knowledgeBaseApi';
import type {
  CommunityTemplateCreate,
  CommunityTemplateGalleryRead,
  CommunityTemplateImportRead,
  CommunityTemplateRead,
  CommunityTemplateUpdate,
} from '../../types/api';

/**
 * Community templates: a member's own finished design plus the zone map saying
 * where the caption, the logo and the optional portrait and value land on it.
 *
 * Two surfaces, deliberately different in scope. Authoring is business-scoped
 * like every other owned record (SRS §4 ownership root) and shows the author
 * every status of their own work. The **gallery** is not scoped to anything: it
 * is the approved templates of every tenant, which is the whole point of the
 * feature, so it hangs off the top-level path like `/vibes` and `/slide-themes`.
 */

function templatesPath(businessId: string): string {
  return `/api/v1/businesses/${businessId}/community-templates`;
}

/**
 * The author's own templates as a collection — the same REST shape the
 * knowledge base uses (`GET`, `POST`, `PUT /{id}`, `DELETE /{id}`), so the list
 * screen gets `useCollection`'s optimistic patches and undo window for free.
 *
 * `submit` is the one verb outside that shape; it is a call of its own below.
 */
export const communityTemplatesApi = createCollectionApi<
  CommunityTemplateRead,
  CommunityTemplateCreate,
  CommunityTemplateUpdate
>('community-templates');

/** One of this business's templates, whatever its status. 404 once it is not. */
export function fetchCommunityTemplate(
  businessId: string,
  templateId: string,
  signal?: AbortSignal,
): Promise<CommunityTemplateRead> {
  return apiClient.get<CommunityTemplateRead>(`${templatesPath(businessId)}/${templateId}`, {
    signal,
  });
}

/**
 * Files a new template as a draft. `attestation` is a literal `true` the author
 * ticked — the backend refuses the call without it, so the checkbox is a real
 * gate rather than a form ornament.
 */
export function createCommunityTemplate(
  businessId: string,
  body: CommunityTemplateCreate,
): Promise<CommunityTemplateRead> {
  return apiClient.post<CommunityTemplateRead>(templatesPath(businessId), { body });
}

/**
 * Saves changes to a draft or a rejected template. Any subset of the four
 * editable fields, at least one. Anything else answers 409
 * `REQUEST_NOT_EDITABLE` — a template under review or already approved is not
 * the author's to move.
 */
export function updateCommunityTemplate(
  businessId: string,
  templateId: string,
  body: CommunityTemplateUpdate,
): Promise<CommunityTemplateRead> {
  return apiClient.put<CommunityTemplateRead>(`${templatesPath(businessId)}/${templateId}`, {
    body,
  });
}

/** Deletes one of the author's own templates. */
export function deleteCommunityTemplate(businessId: string, templateId: string): Promise<void> {
  return apiClient.delete<void>(`${templatesPath(businessId)}/${templateId}`);
}

/**
 * Hands a draft to the operators: the status becomes `pending` and the template
 * stops being editable. Same 409 `REQUEST_NOT_EDITABLE` rule as the update.
 */
export function submitCommunityTemplate(
  businessId: string,
  templateId: string,
): Promise<CommunityTemplateRead> {
  return apiClient.post<CommunityTemplateRead>(`${templatesPath(businessId)}/${templateId}/submit`);
}

/**
 * Turns an exported document into a design to place zones on: the server
 * rasterises page 1, puts the PNG in this business's own assets and records the
 * row, so the `image_path` that comes back is used exactly like an upload's.
 *
 * The one call in the feature that ships bytes through the API rather than
 * straight to Storage — the rasterising is the server's, so the file has to
 * reach it. Domain failure: 422 `TEMPLATE_IMPORT_INVALID`.
 */
export function importCommunityTemplateDocument(
  businessId: string,
  file: File,
): Promise<CommunityTemplateImportRead> {
  const body = new FormData();
  body.append('file', file);
  return apiClient.post<CommunityTemplateImportRead>(`${templatesPath(businessId)}/import`, {
    body,
  });
}

/**
 * The global gallery: approved templates from every tenant, most used first.
 *
 * Cross-tenant on purpose and by design — a template is published to everyone
 * or to nobody — so the path carries no business id and `preview_url` is a
 * public image URL rather than a signed one.
 */
export function listCommunityTemplates(
  signal?: AbortSignal,
): Promise<CommunityTemplateGalleryRead[]> {
  return apiClient.get<CommunityTemplateGalleryRead[]>('/api/v1/community-templates', { signal });
}
